import type { ThemeKey, ThemeVariantStyles } from 'Src/types/theme';
import type { ReactNode } from 'react';
import React from 'react';
import { classnames } from 'Utils/classnames/classnames.utils';
import type { IconName } from 'Components/frameworks/icon/Icon';
import { Icon } from 'Components/frameworks/icon/Icon';
import type { TrackingPayload } from 'Tracking/tracking-api/useTracking';
import { useTracking } from 'Tracking/tracking-api/useTracking';
import { themes } from './ActionButtonsGroup.themes';
export type ActionButtonsGroupItemProps = {
  ariaLabel?: string | null | undefined;
  buttonClassName?: boolean;
  dataTestId?: string;
  iconIsNotSquare?: boolean;
  iconName?: IconName;
  id: string;
  isActive?: boolean;
  label: ReactNode;
  minWidth?: string;
  number?: number;
  onClick: () => void;
  trackingData?: TrackingPayload;
};
type Props = {
  className?: string;
  isExclusiveCollection?: boolean;
  isScrollable?: boolean;
  items: Array<ActionButtonsGroupItemProps>;
  minWidth?: string;
  reverseLabelAndNumber?: boolean;
  theme?: ThemeKey<typeof themes>;
};
export const ActionButtonsGroup = ({
  className,
  isExclusiveCollection,
  isScrollable,
  items = [],
  minWidth: minWidthProp = 'initial',
  reverseLabelAndNumber = true,
  theme = 'default',
}: Props) => {
  const track = useTracking();

  if (!items.length) {
    return null;
  }

  const {
    thRoot,
    thButton,
    thButtonActive,
    thIcon,
    thIconLgSize,
    thIconSize,
    thText,
    thHideLabel,
    thOverrideMinWidth,
    thNumber,
  } = themes({
    isExclusiveCollection,
    isScrollable,
    reverseLabelAndNumber,
  })[theme] as ThemeVariantStyles<typeof themes> & {
    thHideLabel?: boolean;
    thOverrideMinWidth?: boolean;
  };
  return (
    <div className={classnames(thRoot, className)}>
      {items.map(
        ({
          ariaLabel,
          buttonClassName,
          dataTestId,
          iconIsNotSquare,
          iconName,
          id,
          isActive,
          label,
          minWidth,
          number,
          onClick,
          trackingData,
        }) => (
          <button
            aria-disabled={isActive}
            aria-label={ariaLabel}
            className={classnames(
              buttonClassName,
              {
                [thButtonActive]: isActive,
              },
              {
                [thButton]: !isActive,
              },
            )}
            data-testid={dataTestId}
            key={`ActionButtonsGroup-${id}`}
            onClick={() => {
              onClick?.();
              trackingData && track(trackingData);
            }}
            style={{
              minWidth: thOverrideMinWidth ? 'initial' : minWidth || minWidthProp,
            }}
            type="button"
          >
            {iconName && (
              <Icon
                className={thIcon}
                isNotSquare={iconIsNotSquare}
                name={iconName}
                width={iconIsNotSquare ? thIconLgSize : thIconSize}
              />
            )}
            {label && !thHideLabel && (
              <span className={thText}>
                {number && <span className={thNumber}>{`${number} `}</span>}
                {label}
              </span>
            )}
          </button>
        ),
      )}
    </div>
  );
};
